import clsx from "clsx";
import { Chip, EngineChips } from "./Chip";
import { IncidentStatusChip } from "./IncidentStatusChip";
import { lowVolumeHint, StateChipBare } from "./StateChip";
import { LoadError } from "./LoadError";
import { LockButton } from "./LockButton";
import { RuleSquare, toneFromFleetCell } from "./RuleSquare";
import { WebsiteLink } from "./WebsiteLink";
import { useStickyCollapsedSet } from "../hooks/useStickyCollapse";
import {
  daysSince,
  daysSinceDay,
  fmtActor,
  fmtDate,
  fmtMoney,
  fmtNum,
  fmtPct,
  isMachineActor,
  toHotelWebsite,
} from "../format";
import {
  bookingsTone,
  capture,
  CONV_RATE_HINT,
  expectedBookings,
  expectedBookingsHint,
  PAID_CAPTURE_HINT,
  paidCaptureTone,
  ratio,
  Tone,
  TONE_CLASS,
  valueTone,
  windowOf,
  type WindowMode,
} from "../metrics";
import {
  ENGINE_UNPROVEN_HINT,
  ENGINE_UNPROVEN_TAG,
  RULE_PLAIN_DESCRIPTIONS,
  RULE_RELIEVED_EXPLANATION,
  RULE_RESULT_EXPLANATIONS,
  RULE_STANDING_DISPLAY,
  RULE_STANDING_EXPLANATION,
  RULE_STANDING_VERIFYING_DISPLAY,
  RULE_STANDING_VERIFYING_EXPLANATION,
  RULE_WARNING_NAMES,
} from "../vocabulary";
import type {
  ConditionRow,
  FleetMetricsRow,
  HotelListRow,
  IncidentLite,
  RuleId,
  TrackingLockKind,
} from "../types";

/**
 * The flattened fleet table — census.html's structure as a live surface: ONE
 * table, rows grouped by state with a header row per group (Unhealthy →
 * Monitoring → Healthy → Low volume → Paused → Undetermined), census's metric columns fed
 * by the fleet-metrics read (windows end at each source's own data-through
 * day), and the locks column carrying its own quick actions so an operator
 * can place or lift a lock without leaving the dashboard.
 */

export type { WindowMode };

export interface LockActionRequest {
  customerId: string;
  hotelName: string;
  lock: TrackingLockKind;
  action: "apply" | "remove";
}

/**
 * Sticky geometry, census.html's: the column header pins to the top of the
 * page's scroll area and each group header pins directly beneath it. The two
 * offsets must agree on ONE number, so the header's height is fixed here
 * rather than left to grow with its content (the windowed columns carry a
 * second line, which would otherwise decide it).
 *
 * The table is deliberately NOT inside an overflow container — any scrolling
 * ancestor becomes the containing block for `position: sticky` and the header
 * silently stops sticking. The page scrolls; the header pins.
 */
/**
 * ONLY the column header pins. The group bands deliberately do NOT — measured,
 * not assumed: Chrome resolves a sticky `td` against the TABLE, not its
 * `<tbody>`, so every group band pins at the same offset and simply stacks on
 * the one before it (splitting the groups into one tbody each changes nothing —
 * both constructions report both bands parked at y=56 through the whole
 * scroll). census.html carries the same construction and the same artifact.
 * Non-sticky bands scroll away in turn, which is what the eye expects.
 *
 * `border-separate` is likewise load-bearing rather than taste: Tailwind's
 * preflight sets `border-collapse: collapse`, and a collapsed border model
 * gives Chrome no reliable way to travel a border with a sticky cell — so the
 * header carries its hairline as a box-shadow, as census does for the same
 * reason.
 */
export const TABLE_CLASS = "w-full border-separate border-spacing-0";

/**
 * `z-[1]`, not Tailwind's habitual `z-30`. This app's stacking scale is an
 * order of magnitude shorter than Tailwind's defaults — the side drawer is
 * `z-[2]`/`z-[3]`, the fixed top bar `z-[9]`, popup modals `z-[99]` — so a
 * `z-30` header outranks every overlay in the product. It showed as the table
 * head staying lit while the rest of the page dimmed behind an open drawer.
 *
 * All this header has to beat is its own table: the rows and the group bands
 * carry no z-index at all. One is enough.
 */
export const TABLE_HEADER_Z = "z-[1]";

const TH_CLASS = `sticky top-0 ${TABLE_HEADER_Z} h-[56px] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-[#F4F4F6] whitespace-nowrap shadow-[0_1px_0_#E9E9EB]`;
const TD_CLASS = "px-3 py-3 border-b border-b-[#E9E9EB] text-sm align-top";
export const GROUP_TD_CLASS =
  "px-3 py-1.5 bg-[#F2F4F6] border-y border-[#E9E9EB] text-[11.5px] font-semibold uppercase tracking-wider text-gray-500";

/** The card the table sits in: `w-fit min-w-full` so the white background
 *  grows with a table wider than the viewport instead of ending mid-row. */
export const TABLE_CARD_CLASS =
  "bg-white rounded-lg shadow-sm border border-[#E9E9EB] min-w-full w-fit";
const NUM_TD_CLASS = clsx(TD_CLASS, "text-right whitespace-nowrap");

// ─── Grouping (census staterow order — onboarding renders above this table) ──

type GroupKey =
  | "unhealthy"
  | "monitoring"
  | "healthy"
  | "low-volume"
  | "paused"
  | "undetermined";

/** Low volume sits ABOVE Paused, per the contract's own field order: a
 *  low-volume hotel is still serving and still tracked, and a parked one is
 *  neither — so the operator meets it first. */
const GROUP_ORDER: GroupKey[] = [
  "unhealthy",
  "monitoring",
  "healthy",
  "low-volume",
  "paused",
  "undetermined",
];

/**
 * Groups that start collapsed (ADR 0041). Paused is the only one: it is now an
 * Ads-side fact and nothing else — campaigns exist, none is ENABLED — because
 * the hotels that used to fill it when somebody parked them there are excluded
 * from Tracking Health entirely now, through the CRM's test mark. What is left
 * is a small standing residue nobody works day to day.
 *
 * The section is KEPT, not deleted: the fleet counter is computed
 * independently of GROUP_ORDER, so removing a group would make the table claim
 * more rows than it shows, and the KPI tile's scroll-to-section is
 * optional-chained and would silently do nothing. A collapsed group still
 * shows its count in the header, so it never hides how big it is.
 *
 * Applied on first visit only — see useStickyCollapsedSet.
 */
const DEFAULT_COLLAPSED_GROUPS: readonly GroupKey[] = ["paused"];

const GROUP_LABEL: Record<GroupKey, string> = {
  unhealthy: "Unhealthy",
  monitoring: "Monitoring",
  healthy: "Healthy",
  "low-volume": "Low volume",
  paused: "Paused",
  undetermined: "Undetermined",
};

export function groupOf(row: HotelListRow): GroupKey {
  const reported = row.status.reported;
  if (reported === "unhealthy") return "unhealthy";
  if (reported === "monitoring") return "monitoring";
  if (reported === "healthy") return "healthy";
  if (reported === "low-volume") return "low-volume";
  if (reported === "paused") return "paused";
  return "undetermined";
}

/** The anchor id a KPI tile scrolls to. */
export function sectionAnchorId(key: string): string {
  return `th-sec-${key}`;
}

function groupExtra(
  key: GroupKey,
  rows: HotelListRow[],
  metricsByCid: Map<string, FleetMetricsRow> | null,
): string {
  if (key === "unhealthy") {
    if (!metricsByCid) return "";
    let atRisk = 0;
    for (const row of rows) {
      atRisk += metricsByCid.get(row.customerId)?.full.cost ?? 0;
    }
    return ` · ${fmtMoney(atRisk)} of 90-day spend at risk`;
  }
  if (key === "monitoring") return "";
  if (key === "healthy")
    return " · promotion criterion met — qualifying booking since the trusted data start date";
  if (key === "low-volume")
    return " · Healthy hotels booking too little for the rules to judge (derived daily), plus any an operator placed here — the booking rules stand down for them";
  if (key === "paused")
    return " · every campaign switched off — nothing spending, outside the health states";
  return " · no derivation recorded yet";
}

/** Days in the current Monitoring spell (the held clock's own anchor). */
function heldDays(row: HotelListRow): number | null {
  if (row.status.reported !== "monitoring") return null;
  return daysSince(row.status.stateChangedAt);
}

// ─── Metric cells (derivations live in ../metrics, shared with the hotel
//     page's tiles so the two surfaces cannot tone the same number apart) ─────

function MetricCell({
  main,
  alt,
  tone = "",
  title,
}: {
  main: string;
  alt: string;
  tone?: Tone;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
}) {
  return (
    <td className={NUM_TD_CLASS} data-tip={title}>
      <div className={clsx("tabular-nums", TONE_CLASS[tone])}>{main}</div>
      <div className="text-[11px] text-gray-400 tabular-nums">{alt}</div>
    </td>
  );
}

// ─── Rules R1–R6 chips ────────────────────────────────────────────────────────
// The cells arrive keyed by rule string from the fleet-metrics read, so a
// rule the API starts serving renders the moment it is listed here.

const ALL_RULES: RuleId[] = ["1", "2", "3", "4", "5", "6"];

/** The active rule-scoped condition relieving this rule, when one stands. */
function relievingCondition(
  conditions: ConditionRow[],
  ruleId: RuleId,
): ConditionRow | undefined {
  return conditions.find(
    (condition) =>
      condition.status === "active" &&
      condition.scopeType === "rule" &&
      condition.scopeId === ruleId,
  );
}

/** The full explainer a hover carries: what the rule watches, what this
 *  result means, and — for a relieved rule — WHY a human accepted it. */
function ruleChipTitle(
  ruleId: RuleId,
  cell: FleetMetricsRow["rules"][number] | undefined,
  conditions: ConditionRow[],
): string {
  const head = `Rule ${ruleId} — ${RULE_WARNING_NAMES[ruleId]}`;
  const watches = `Watches: ${RULE_PLAIN_DESCRIPTIONS[ruleId]}.`;
  if (!cell) return `${head}: never evaluated yet\n${watches}`;
  const when = `evaluated ${fmtDate(cell.evaluatedAt)}`;
  // A standing warning outranks the result here exactly as it does on the
  // hotel page — otherwise a hotel whose Rule 2 fired shows a green `2` in the
  // fleet list the very next day, with nothing fixed.
  if (cell.standing && !cell.mapsToAcceptedCondition) {
    return [
      `${head}: ${
        cell.standing.unresolved
          ? RULE_STANDING_DISPLAY.toLowerCase()
          : RULE_STANDING_VERIFYING_DISPLAY.toLowerCase()
      } since ${fmtDate(cell.standing.firstDetectedAt)}`,
      cell.standing.unresolved
        ? RULE_STANDING_EXPLANATION
        : RULE_STANDING_VERIFYING_EXPLANATION,
      `Latest read: ${cell.result} · ${when}`,
      watches,
    ].join("\n");
  }
  if (cell.result === "fail" && cell.mapsToAcceptedCondition) {
    const condition = relievingCondition(conditions, ruleId);
    return [
      `${head}: fails — relieved by an accepted condition · ${when}`,
      RULE_RELIEVED_EXPLANATION,
      condition ? `Why accepted: ${condition.reason}` : null,
      condition?.exitCondition ? `Exit: ${condition.exitCondition}` : null,
      watches,
    ]
      .filter(Boolean)
      .join("\n");
  }
  const meaning =
    RULE_RESULT_EXPLANATIONS[
      cell.result as keyof typeof RULE_RESULT_EXPLANATIONS
    ] ?? "";
  const resultLabel =
    cell.result === "insufficient" ? "insufficient data" : cell.result;
  return `${head}: ${resultLabel} · ${when}\n${resultLabel === "pass" ? "Pass" : resultLabel === "fail" ? "Fail" : "Insufficient"}: ${meaning}.\n${watches}`;
}

function RuleChips({
  metrics,
  conditions,
}: {
  metrics: FleetMetricsRow | undefined;
  conditions: ConditionRow[];
}) {
  if (!metrics) return <span className="text-gray-400 text-xs">—</span>;
  const byRule = new Map(metrics.rules.map((cell) => [cell.rule, cell]));
  return (
    <span className="inline-flex items-center gap-1">
      {ALL_RULES.map((ruleId) => {
        const cell = byRule.get(ruleId);
        return (
          <RuleSquare
            key={ruleId}
            rule={ruleId}
            tone={toneFromFleetCell(cell)}
            title={ruleChipTitle(ruleId, cell, conditions)}
          />
        );
      })}
    </span>
  );
}

// ─── Locks column — identify AND act, in place ────────────────────────────────

function RowLockButton({
  row,
  lock,
  onLockAction,
}: {
  row: HotelListRow;
  lock: TrackingLockKind;
  onLockAction: (request: LockActionRequest) => void;
}) {
  const facts = lock === "rules" ? row.locks.rules : row.locks.validator;
  return (
    <LockButton
      lock={lock}
      facts={facts}
      revealOnHover
      onClick={() =>
        onLockAction({
          customerId: row.customerId,
          hotelName: row.hotelName,
          lock,
          action: facts.locked ? "remove" : "apply",
        })
      }
    />
  );
}

// ─── The table ────────────────────────────────────────────────────────────────

export function FleetTable({
  rows,
  metricsByCid,
  metricsUnavailable,
  mode,
  recentDays,
  isLoading,
  error,
  retry,
  onOpenHotel,
  onLockAction,
  onEditNote,
}: {
  /** Live-state rows only — the onboarding section renders separately. */
  rows: HotelListRow[];
  metricsByCid: Map<string, FleetMetricsRow> | null;
  metricsUnavailable: boolean;
  mode: WindowMode;
  recentDays: number;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
  onOpenHotel: (customerId: string) => void;
  onLockAction: (request: LockActionRequest) => void;
  onEditNote: (row: HotelListRow) => void;
}) {
  // Collapsed groups (header click) — the header row stays as the section's
  // anchor and count, so a KPI tile still scrolls to a collapsed group.
  //
  // Sticky across visits: which groups someone works is a standing fact about
  // that person (an operator living in Unhealthy collapses the other four
  // once, not once a day). Safe to persist precisely because the header row
  // keeps the count — a collapsed group never hides how big it is.
  const [collapsedGroups, toggleGroup] = useStickyCollapsedSet<GroupKey>(
    "fleet-groups",
    DEFAULT_COLLAPSED_GROUPS,
  );

  if (error) {
    return <LoadError error={error} label="the fleet" onRetry={retry} />;
  }

  // Group in census order; census sorted within a group by 90-day spend.
  const grouped = GROUP_ORDER.map((key) => ({
    key,
    rows: rows
      .filter((row) => groupOf(row) === key)
      .sort((a, b) => {
        const costA = metricsByCid?.get(a.customerId)?.full.cost ?? a.spend30d;
        const costB = metricsByCid?.get(b.customerId)?.full.cost ?? b.spend30d;
        return costB - costA;
      }),
  })).filter((group) => group.rows.length > 0);

  const windowSub =
    mode === "recent"
      ? `${recentDays}d · 90d below`
      : `90d · ${recentDays}d below`;
  const windowedHeader = (label: string) => (
    <th className={clsx(TH_CLASS, "text-right")}>
      {label}
      <span className="block mt-0.5 text-[9.5px] font-medium normal-case tracking-normal text-gray-400">
        {windowSub}
      </span>
    </th>
  );

  return (
    <div className={TABLE_CARD_CLASS}>
      {metricsUnavailable && (
        <div className="px-4 py-2 text-xs text-warning-dark border-b border-[#E9E9EB]">
          Couldn't load the metric columns — the fleet still renders from the
          status read.
        </div>
      )}
      <table className={clsx(TABLE_CLASS, "min-w-[1280px]")}>
        <thead>
          <tr>
            <th className={clsx(TH_CLASS, "rounded-tl-lg")}>State</th>
            <th
              className={TH_CLASS}
              data-tip="The incident's blocked-on note, in a couple of words — what this hotel is waiting on"
            >
              Waiting on
            </th>
            <th className={TH_CLASS}>Hotel</th>
            <th className={TH_CLASS}>Engine</th>
            <th
              className={TH_CLASS}
              data-tip="Latest evaluation per health rule — R6 is the click floor, an Ads-side rule"
            >
              Rules R1–R6
            </th>
            <th
              className={TH_CLASS}
              data-tip="Rules / Validator locks — click to apply or remove"
            >
              Locks
            </th>
            {windowedHeader("Clicks")}
            {windowedHeader("Bookings")}
            {windowedHeader("Value")}
            {windowedHeader("Conv rate")}
            {windowedHeader("Paid sess. / clicks")}
            <th className={clsx(TH_CLASS, "text-right rounded-tr-lg")}>
              Live for
            </th>
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            grouped.map((group) => (
              <GroupRows
                key={group.key}
                groupKey={group.key}
                rows={group.rows}
                metricsByCid={metricsByCid}
                mode={mode}
                collapsed={collapsedGroups.has(group.key)}
                onToggle={() => toggleGroup(group.key)}
                onOpenHotel={onOpenHotel}
                onLockAction={onLockAction}
                onEditNote={onEditNote}
              />
            ))}
        </tbody>
      </table>
      {isLoading && (
        <div className="p-8 text-center text-content-secondary text-sm">
          Loading…
        </div>
      )}
      {!isLoading && rows.length === 0 && (
        <div className="flex flex-col items-center py-14">
          <span className="text-content-tertiary text-base font-medium">
            No hotels match
          </span>
        </div>
      )}
    </div>
  );
}

function GroupRows({
  groupKey,
  rows,
  metricsByCid,
  mode,
  collapsed,
  onToggle,
  onOpenHotel,
  onLockAction,
  onEditNote,
}: {
  groupKey: GroupKey;
  rows: HotelListRow[];
  metricsByCid: Map<string, FleetMetricsRow> | null;
  mode: WindowMode;
  collapsed: boolean;
  onToggle: () => void;
  onOpenHotel: (customerId: string) => void;
  onLockAction: (request: LockActionRequest) => void;
  onEditNote: (row: HotelListRow) => void;
}) {
  return (
    <>
      <tr
        id={sectionAnchorId(groupKey)}
        className="scroll-mt-2 cursor-pointer select-none"
        onClick={onToggle}
        data-tip={collapsed ? "Expand this group" : "Collapse this group"}
      >
        <td
          colSpan={12}
          className={clsx(
            GROUP_TD_CLASS,
            "hover:text-content-primary transition-colors",
          )}
        >
          <span
            className={clsx(
              "inline-block w-3 mr-1 text-[10px] transition-transform",
              !collapsed && "rotate-90",
            )}
          >
            ▶
          </span>
          {GROUP_LABEL[groupKey]} ({rows.length})
          <span className="normal-case font-medium tracking-normal">
            {groupExtra(groupKey, rows, metricsByCid)}
            {collapsed && " · collapsed"}
          </span>
        </td>
      </tr>
      {!collapsed &&
        rows.map((row) => (
          <HotelRow
            key={row.customerId}
            row={row}
            metrics={metricsByCid?.get(row.customerId)}
            mode={mode}
            onOpenHotel={onOpenHotel}
            onLockAction={onLockAction}
            onEditNote={onEditNote}
          />
        ))}
    </>
  );
}

/**
 * The blocked-on note a PERSON recorded, or null.
 *
 * The solver stamps `blockedOnNote` itself whenever it derives a hand-off
 * status, with the plan's next step in the catalog's words ("Next step
 * (manual): …") and `system:solver` as the author. That is the machine
 * restating its own plan, not an operator saying what the hotel is waiting on
 * — so this column, which asks the second question, does not count it. Such a
 * row reads as having no note yet and offers to take one.
 */
export function operatorBlockedOnNote(incident: IncidentLite): string | null {
  if (!incident.blockedOnNote) return null;
  return isMachineActor(incident.blockedOnNoteBy)
    ? null
    : incident.blockedOnNote;
}

/**
 * The incident's blocked-on note as a row-level tag — the one fact an operator
 * scanning Unhealthy wants without opening the hotel. Only a row carrying an
 * open incident can hold one: the note lives ON the incident, and writing it
 * resets that incident's 7-day reminder clock (see `useLogNote`).
 */
function WaitingOnCell({
  row,
  onEditNote,
}: {
  row: HotelListRow;
  onEditNote: (row: HotelListRow) => void;
}) {
  const incident = row.openIncident;
  if (!incident) return <span className="text-gray-300 text-xs">—</span>;

  const note = operatorBlockedOnNote(incident);

  // The row itself opens the hotel, so the tag must not let the click through.
  const openNoteEditor = (event: React.MouseEvent) => {
    event.stopPropagation();
    onEditNote(row);
  };

  if (!note) {
    return (
      <button
        onClick={openNoteEditor}
        data-tip="Record what this hotel is waiting on"
        className="text-xs text-gray-400 hover:text-brand-primary transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        + note
      </button>
    );
  }

  return (
    <button
      onClick={openNoteEditor}
      data-tip={waitingOnTip(incident, note)}
      className="block max-w-[11rem] text-left"
    >
      <Chip tone="orange" className="max-w-full hover:brightness-95">
        {/* Two lines, then ellipsis. `whitespace-normal` is set on the span
         *  rather than the Chip deliberately: the Chip's own
         *  `whitespace-nowrap` reaches this text by INHERITANCE, and a rule
         *  applied directly to the child beats an inherited value whatever
         *  order the two utilities land in the stylesheet — overriding it on
         *  the Chip instead would be a source-order coin flip. */}
        <span className="whitespace-normal line-clamp-2">{note}</span>
      </Chip>
    </button>
  );
}

/** The tag's hover: the note in full (the tag clips it at two lines), whose
 *  sentence it is, and how old. */
function waitingOnTip(incident: IncidentLite, note: string): string {
  const who = fmtActor(incident.blockedOnNoteBy) || "unknown";
  const age = incident.blockedOnNoteAgeDays;
  const when = age == null ? "" : age === 0 ? " · today" : ` · ${age}d ago`;
  return `“${note}” — ${who}${when}`;
}

function HotelRow({
  row,
  metrics,
  mode,
  onOpenHotel,
  onLockAction,
  onEditNote,
}: {
  row: HotelListRow;
  metrics: FleetMetricsRow | undefined;
  mode: WindowMode;
  onOpenHotel: (customerId: string) => void;
  onLockAction: (request: LockActionRequest) => void;
  onEditNote: (row: HotelListRow) => void;
}) {
  const main = windowOf(metrics, mode);
  const alt = windowOf(metrics, mode === "recent" ? "full" : "recent");
  const currency = metrics?.currency ?? row.currency;

  // Census tones: a zero where the hotel's own rate expected several is the
  // signal; value pairs with bookings to catch valueless recording; the paid
  // capture alerts on Rule 3's own thresholds (100+ clicks, < 40%).
  const expected = expectedBookings(metrics, mode);
  const paidMain = capture(main?.ga4PaidSessions, main?.clicks);

  const held = heldDays(row);
  const liveFor = daysSinceDay(metrics?.adsStart ?? null);
  const website = toHotelWebsite(row.websiteUrl);

  return (
    <tr
      className="cursor-pointer hover:bg-gray-50 transition-colors group"
      onClick={() => onOpenHotel(row.customerId)}
    >
      <td className={TD_CLASS}>
        <div className="flex flex-col items-start gap-1">
          {/* Reported state only — the "(computed …)" annotation is dropped
              from the table (operator decision 2026-08-15); the hotel detail
              header still renders the full StateChip with it. The row does
              hold the status facts, so a Low-volume chip's hover says which
              Low volume it is (derived daily vs operator-placed). */}
          <StateChipBare
            state={row.status.reported}
            hint={lowVolumeHint(row.status)}
          />
          {held != null && held >= 30 && (
            <Chip tone="orange" title="days in the current Monitoring spell">
              held {held}d
            </Chip>
          )}
        </div>
      </td>
      <td className={TD_CLASS}>
        <WaitingOnCell row={row} onEditNote={onEditNote} />
      </td>
      <td className={TD_CLASS}>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium text-content-primary">
            {row.hotelName}
          </span>
          {/* Outcomes only (2026-08-17): a `fix_applied` row still says so —
              it is the one status that tells a scanner to leave the row alone —
              while every active status chips nothing, since naming a phase of
              our own plan answered none of the questions this row is scanned
              for. The State and Waiting-on columns carry those. */}
          {row.openIncident && (
            <IncidentStatusChip incident={row.openIncident} />
          )}
          {/* A silenced incident keeps its row, its group and its sort key —
              only the Slack reminder is paused, so this table is exactly the
              surface that must still show it (ADR 0017). Never filter on it. */}
          {row.openIncident?.silence?.state === "active" && (
            <Chip
              tone="neutral"
              title={`Reminder silenced until ${row.openIncident.silence.until.slice(0, 10)}${
                row.openIncident.silence.note
                  ? ` — “${row.openIncident.silence.note}”`
                  : ""
              } (${fmtActor(row.openIncident.silence.silencedBy) || "unknown"})`}
            >
              🔕 silenced
            </Chip>
          )}
          {/* No hotel-level `condition` tag (removed 2026-08-15). A count of
              accepted conditions names a property of our own records, not of
              the hotel, and answers none of the questions a row is scanned
              for — which rule, why, what now. Where a condition CHANGES a
              reading it is rendered at that reading: the relieved rule square
              is toned for it and its hover carries the reason and the exit
              condition (`ruleChipTitle`). `row.conditions` still feeds that. */}
        </div>
        <div className="text-xs text-content-secondary">
          {website ? (
            <WebsiteLink website={website} />
          ) : (
            (row.ownerEmail ?? row.customerId)
          )}
        </div>
      </td>
      <td className={TD_CLASS}>
        <span className="inline-flex items-center gap-1 flex-wrap">
          <EngineChips engines={row.engines} trackingMode={row.trackingMode} />
          {row.engineUnproven && (
            <Chip tone="purple" title={ENGINE_UNPROVEN_HINT}>
              {ENGINE_UNPROVEN_TAG}
            </Chip>
          )}
        </span>
      </td>
      <td className={TD_CLASS}>
        <RuleChips metrics={metrics} conditions={row.conditions} />
      </td>
      <td className={TD_CLASS}>
        <span className="inline-flex items-center gap-1">
          <RowLockButton row={row} lock="rules" onLockAction={onLockAction} />
          <RowLockButton
            row={row}
            lock="validator"
            onLockAction={onLockAction}
          />
        </span>
      </td>
      <MetricCell
        main={fmtNum(main?.clicks)}
        alt={fmtNum(alt?.clicks)}
        tone={main?.clicks == null ? "zero" : ""}
      />
      <MetricCell
        main={fmtNum(main?.conversions)}
        alt={fmtNum(alt?.conversions)}
        tone={bookingsTone(main, expected)}
        title={expectedBookingsHint(expected)}
      />
      <MetricCell
        main={
          main?.conversionsValue == null
            ? "—"
            : fmtMoney(main.conversionsValue, currency)
        }
        alt={
          alt?.conversionsValue == null
            ? "—"
            : fmtMoney(alt.conversionsValue, currency)
        }
        tone={valueTone(main)}
      />
      <MetricCell
        main={fmtPct(ratio(main?.conversions, main?.clicks), 1)}
        alt={fmtPct(ratio(alt?.conversions, alt?.clicks), 1)}
        title={CONV_RATE_HINT}
      />
      <MetricCell
        main={fmtPct(paidMain)}
        alt={fmtPct(capture(alt?.ga4PaidSessions, alt?.clicks))}
        tone={paidCaptureTone(main)}
        title={PAID_CAPTURE_HINT}
      />
      <td
        className={NUM_TD_CLASS}
        data-tip={
          metrics?.adsStart
            ? `first served impression ${metrics.adsStart}`
            : "ads-start unknown"
        }
      >
        {liveFor == null ? (
          <span className="text-gray-400">—</span>
        ) : (
          `${liveFor}d`
        )}
      </td>
    </tr>
  );
}
